import { useLocation, useSearchParams } from "react-router-dom";

/**
 * Syncs an antd Tabs active key with a URL search param so browser Back
 * restores the tab the admin was on. The key is written with `replace`, so
 * clicking through tabs doesn't stack history entries; the current tab is
 * simply carried by whatever navigation happens next. Sibling params are
 * preserved (writing `?tab=` keeps `?sub=` and vice versa).
 *
 * `validKeys` sanitizes a stale or hand-edited param: a controlled Tabs with
 * an unknown activeKey would render no pane at all, so anything outside the
 * list falls back to `defaultKey`.
 *
 * Wire as: <Tabs activeKey={activeKey} onChange={onChange} ... />
 */
export const useTabParam = (
  defaultKey: string,
  paramName = "tab",
  validKeys?: readonly string[],
) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(paramName);
  const activeKey =
    raw && (!validKeys || validKeys.includes(raw)) ? raw : defaultKey;

  const onChange = (key: string) => {
    const params = new URLSearchParams(searchParams);
    params.set(paramName, key);
    // Preserve whatever location.state the page arrived with — a
    // setSearchParams/navigate call that omits `state` defaults it to
    // undefined rather than carrying the current one forward, which would
    // otherwise silently drop state a caller navigated in with (e.g. the
    // create-schedule drawer's resume draft) the moment the admin switches
    // tabs, before they've done anything else.
    setSearchParams(params, { replace: true, state: location.state });
  };

  return { activeKey, onChange };
};
